import { MAX_FILES_PER_SELECTION } from '@flowshare/protocol';

function attachRelativePath(file: File, path: string): File {
  if (!path) return file;
  try {
    Object.defineProperty(file, 'relativePath', {
      value: path,
      configurable: true,
      enumerable: false,
    });
  } catch {
    // Some browsers make File objects non-extensible; the file still transfers.
  }
  return file;
}

function readFileEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirectoryBatch(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

async function walkEntry(
  entry: FileSystemEntry,
  prefix: string,
  output: File[],
  depth = 0,
): Promise<void> {
  if (output.length >= MAX_FILES_PER_SELECTION || depth > 16) return;

  if (entry.isFile) {
    const file = await readFileEntry(entry as FileSystemFileEntry);
    output.push(attachRelativePath(file, prefix ? `${prefix}${file.name}` : ''));
    return;
  }

  if (!entry.isDirectory) return;

  const directory = entry as FileSystemDirectoryEntry;
  const reader = directory.createReader();
  const nextPrefix = prefix ? `${prefix}${entry.name}/` : `${entry.name}/`;

  // readEntries returns at most 100 entries per call and must be drained.
  for (;;) {
    const batch = await readDirectoryBatch(reader);
    if (batch.length === 0) break;
    for (const child of batch) {
      await walkEntry(child, nextPrefix, output, depth + 1);
      if (output.length >= MAX_FILES_PER_SELECTION) return;
    }
  }
}

/**
 * Extract files from a drop event, recursively walking directories when the
 * browser exposes the entries API. Folder drops preserve their relative paths.
 */
export async function filesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const items = Array.from(dataTransfer.items ?? []);
  const supportsEntries = items.some((item) => typeof item.webkitGetAsEntry === 'function');

  if (!supportsEntries) {
    return Array.from(dataTransfer.files).slice(0, MAX_FILES_PER_SELECTION);
  }

  const output: File[] = [];
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (!entry) continue;
    try {
      await walkEntry(entry, '', output);
    } catch {
      // Skip unreadable entries and continue with the rest.
    }
    if (output.length >= MAX_FILES_PER_SELECTION) break;
  }

  if (output.length === 0) {
    return Array.from(dataTransfer.files).slice(0, MAX_FILES_PER_SELECTION);
  }

  return output;
}
